import { env } from './EnvironmentConfig';
import { REQUEST_ID_HEADER } from '../utils/requestId';

// Options shared by instrumentation-client.js, sentry.server.config.js and sentry.edge.config.js.
// GlitchTip speaks the Sentry protocol, so the Sentry SDK is used as-is.

// NEXT_PUBLIC_GLITCHTIP_DSN (build-time) overrides the per-environment value in EnvironmentConfig.
const dsn = process.env.NEXT_PUBLIC_GLITCHTIP_DSN || env.GLITCHTIP_DSN;

const readHeader = (headers, name) => headers?.get?.(name) ?? headers?.[name.toLowerCase()];

// Path only (query strings can carry tokens / emails) with ids collapsed, so
// /order/123 and /order/456 group into one issue.
const toRoute = (url = '') =>
  String(url)
    .replace(/^https?:\/\/[^/]+/i, '')
    .split('?')[0]
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, '/:uuid')
    .replace(/\/\d+(?=\/|$)/g, '/:id');

// Makes API failures searchable by request_id (paste it into the Symfony logs) and groups
// them per endpoint + status instead of all landing in one "AxiosError" issue.
const enrichApiError = (event, hint) => {
  const error = hint?.originalException;
  if (!error?.isAxiosError) return event;

  const method = (error.config?.method || 'get').toUpperCase();
  const route = toRoute(error.config?.url);
  const status = error.response?.status;
  // prefer the id Symfony echoes back (needs Access-Control-Expose-Headers), else the one we sent
  const requestId =
    readHeader(error.response?.headers, REQUEST_ID_HEADER) ||
    readHeader(error.config?.headers, REQUEST_ID_HEADER);

  event.tags = {
    ...event.tags,
    ...(requestId && { request_id: requestId }),
    api_method: method,
    api_route: route,
    ...(status && { api_status: String(status) }),
  };
  event.fingerprint = ['api-error', method, route, String(status ?? error.code ?? 'no-response')];

  const exception = event.exception?.values?.[0];
  if (exception) {
    exception.value = `${method} ${route} failed (${status ?? error.code}): ${error.message}`;
  }
  return event;
};

export const getGlitchTipOptions = () => ({
  dsn,
  // no DSN configured (dev, or not filled in yet) -> SDK stays inert
  enabled: Boolean(dsn),
  environment: env.GLITCHTIP_ENVIRONMENT,
  // GlitchTip's performance support is basic; the docs recommend a low rate.
  tracesSampleRate: 0.01,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'Non-Error promise rejection captured',
    /^Script error\.?$/,
  ],
  // browser extensions and the third-party widgets this site embeds
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari(-web)?-extension:\/\//i,
    /connect\.facebook\.net/i,
    /clarity\.ms/i,
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /chatwoot\.tunisolutions\.com/i,
  ],
  beforeSend: enrichApiError,
});
